import type { ReactNode } from 'react'
import { assetUrl } from '@/shared/utils/assetUrl'
import { Button, EnrollmentHelpText } from '@/shared/components'
import { StepHeader } from '../StepHeader'
import { testPoints } from './utils'

type EnglishProficiencyIntroProps = {
  currentStep: number
  totalSteps: number
  onBack: () => void
  onStartTest: () => void
  isStartingTest: boolean
  startTestError: string | null
}

function ChecklistItem({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-primary bg-primary shadow-[0_0_0_3px_rgba(230, 83, 0,0.18)]">
        <svg
          className="h-3 w-3 text-white"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 6l3 3 5-5" />
        </svg>
      </span>
      <span className="text-base text-gray-800">{children}</span>
    </div>
  )
}

export function EnglishProficiencyIntro({
  currentStep,
  totalSteps,
  onBack,
  onStartTest,
  isStartingTest,
  startTestError
}: EnglishProficiencyIntroProps) {
  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-4xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mx-auto max-w-3xl space-y-6 pt-6">
          <div
            className="animate-fade-in-up flex flex-col items-center gap-6 rounded-2xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 p-6 shadow-lg md:flex-row"
            style={{ animationDelay: '120ms' }}
          >
            <div className="flex-1">
              <h2 className="mb-2 flex items-center gap-2 text-2xl font-bold text-primary">
                <img
                  src={assetUrl('/assets/icons/English-test-illustration.svg')}
                  alt="English Test"
                  className="inline-block h-8 w-8"
                />
                English Proficiency Test
              </h2>
              <p className="text-lg font-medium text-gray-700">
                You must complete a short English proficiency test before continuing.
              </p>
            </div>
          </div>

          <div
            className="animate-fade-in-up grid grid-cols-1 gap-6 md:grid-cols-2"
            style={{ animationDelay: '200ms' }}
          >
            <div className="flex flex-col gap-3 rounded-2xl border-2 border-gray-100 bg-white p-6 shadow-sm">
              <h3 className="mb-2 text-base font-semibold text-primary">What you'll do</h3>
              {testPoints.map((point, index) =>
                point.what ? <ChecklistItem key={index}>{point.what}</ChecklistItem> : null
              )}
            </div>
            <div className="flex flex-col gap-3 rounded-2xl border-2 border-gray-100 bg-white p-6 shadow-sm">
              <h3 className="mb-2 text-base font-semibold text-primary">Why it's important</h3>
              {testPoints.map((point, index) =>
                point.why ? <ChecklistItem key={index}>{point.why}</ChecklistItem> : null
              )}
            </div>
          </div>

          <div
            className="animate-fade-in-up flex flex-col gap-2 rounded-2xl border border-yellow-300 bg-yellow-50 p-6 shadow-md"
            style={{ animationDelay: '280ms' }}
          >
            <div className="mb-1 flex items-center gap-2">
              <svg
                className="h-5 w-5 text-yellow-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>
              <h3 className="text-sm font-semibold uppercase tracking-[0.5px] text-yellow-800">
                Important
              </h3>
            </div>
            <ul className="space-y-1 text-base leading-relaxed text-gray-700">
              <li>Must be completed independently</li>
              <li>No notes or external help allowed</li>
              <li>A waiting period may apply after the allowed attempts</li>
            </ul>
          </div>

          <div className="animate-fade-in-up text-center" style={{ animationDelay: '340ms' }}>
            <p className="text-lg font-medium text-primary">The test will open within this page.</p>
          </div>

          {startTestError ? (
            <div
              className="animate-fade-in-up rounded-2xl border border-rose-200 bg-rose-50 p-4 text-center text-sm font-medium text-rose-700 shadow-sm"
              style={{ animationDelay: '370ms' }}
            >
              {startTestError}
            </div>
          ) : null}

          <div className="animate-fade-in-up mx-auto max-w-md" style={{ animationDelay: '400ms' }}>
            <Button
              type="button"
              onClick={onStartTest}
              fullWidth
              loading={isStartingTest}
              disabled={isStartingTest}
              className="hover:-translate-y-0.5 active:scale-95"
            >
              Start English Test
            </Button>
          </div>

          <div className="animate-fade-in-up text-center" style={{ animationDelay: '460ms' }}>
            <EnrollmentHelpText />
          </div>
        </div>
      </div>
    </div>
  )
}
